<template>
  <div
    class="border-static-default-mid rounded-sm relative overflow-x-scroll border py-24 lg:overflow-x-auto"
  >
    <div class="heading-3 mb-8 px-24">
      {{ i18n(translations.upcomingPayouts) }}
    </div>
    <div class="body-2 mb-8 px-24">
      {{ i18n(translations.upcomingPayoutsInfoText) }}
    </div>
    <RevTable
      v-if="hasUpcomingPayouts"
      :collection="payouts"
      :columns
      transparent-header
    >
      <template #body-payoutDate="{ item }">
        <span data-allow-mismatch>
          {{ i18n.date(new Date(item.invoicing_date), SHORT_DATE_FORMAT) }}
        </span>
      </template>
      <template #body-salesPeriod="{ item }">
        <span data-allow-mismatch>
          {{ i18n.date(new Date(item.period_start), SHORT_DATE_FORMAT) }}
          -
          {{ i18n.date(new Date(item.period_end), SHORT_DATE_FORMAT) }}
        </span>
      </template>
    </RevTable>
    <div
      v-if="!hasUpcomingPayouts"
      class="bg-surface-default-hi rounded-sm body-1-bold mx-24 flex p-8"
    >
      {{ i18n(translations.noUpcomingPayouts) }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { Currency } from '@backmarket/http-api'
import type { SellerWalletPlannedPayout } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevTable } from '@ds/components/Table'

import { SHORT_DATE_FORMAT } from '../../../../Invoices.constants'

import translations from './UpcomingPayouts.translations'

const props = defineProps<{
  info: SellerWalletPlannedPayout[]
}>()

const i18n = useI18n()

// One payout can have only one currency wherease another one could have multiple
// Using Set() avoids duplicates
const currencyColumns = computed(() => {
  return [
    ...new Set(
      props.info.reduce(
        (currencies, payout) => [
          ...currencies,
          ...(Object.keys(payout.amounts) as Currency[]),
        ],
        [] as Currency[],
      ),
    ),
  ]
})

/*
Workaround because the following template triggers a warning:
> Property "currency" was accessed during render but is not defined on instance.

<template
  v-for="currency in currencyColumns"
  :key="currency"
  #[`body-${currency}`]="{ item }"
>
  {{ i18n.price({ amount: item.amounts[currency]!, currency }) }}
</template>
*/
const payouts = computed(() =>
  props.info.map((payout) => ({
    ...payout,
    ...currencyColumns.value.reduce((currencyFields, currency) => {
      const amount = payout.amounts[currency]

      return {
        ...currencyFields,
        ...(amount && { [String(currency)]: i18n.price({ amount, currency }) }),
      }
    }, {}),
  })),
)

const keys = ['payoutDate', 'salesPeriod'] as (keyof typeof translations)[]

const columns = computed(() => [
  // Static columns
  ...keys.map((key) => ({ key, label: i18n(translations[key]) })),
  // Dynamic currency columns
  ...currencyColumns.value.map((currency) => ({
    key: currency,
    label: i18n(translations.amountIn, {
      currency: i18n.currencySign(currency),
    }),
  })),
])

const hasUpcomingPayouts = computed(() => !!props.info.length)
</script>
